import React, { useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  MenuAlt4Icon,
  DocumentIcon,
  DotsVerticalIcon,
  EyeIcon,
  EyeOffIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/outline';
import DropdownPortal from './DropdownPortal';
import { useNavigate } from 'react-router-dom';
import { toastService } from '../../../../../../services/toast';
import DeleteConfirmModal from '../../../../../common/DeleteConfirmModal';
import PermissionGuard from '../../../../../common/PermissionGuard';

export default function Resource({ resource, onEdit, onStatusChange, onDelete }) {
  const [showActions, setShowActions] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const buttonRef = React.useRef(null);
  const dropdownRef = React.useRef(null);
  const navigate = useNavigate();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({
    id: resource.id,
    data: {
      type: 'resource',
      item: resource
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition
  };

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        className={`relative flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-lg
          border border-gray-200 dark:border-gray-700 shadow-sm cursor-default overflow-visible
          ${isDragging ? 'opacity-50 shadow-lg scale-105' : ''}`}
      >
        <div className="flex items-center gap-3 overflow-visible">
          {/* مقبض السحب */}
          <div className="relative group overflow-visible flex items-center">
            <div
              {...attributes}
              {...listeners}
              className="cursor-move text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <MenuAlt4Icon className="w-4 h-4" />
            </div>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              اسحب لإعادة الترتيب
            </div>
          </div>

          {/* أيقونة نوع المحتوى */}
          <div className="relative group overflow-visible flex items-center">
            <DocumentIcon className="w-5 h-5 text-red-500 dark:text-red-400" />
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              حقيبة تعليمية
            </div>
          </div>

          <div className="relative group overflow-visible flex items-center">
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white">
                {resource.title}
              </h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {resource.resource_type === 'file' || resource.type === 'file' ? (
                  <>ملف حقيبة تعليمية</>
                ) : (
                  <>رابط: حقيبة تعليمية</>
                )}
              </p>
            </div>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              عرض تفاصيل الحقيبة
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 overflow-visible">
          {/* أزرار الإجراءات المباشرة - الترتيب: تعديل، نشر، حذف */}
          
          {/* 1. زر التعديل */}
          <PermissionGuard permission="edit_course_content">
            <div className="relative group overflow-visible flex items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // تعديل الحقيبة
                  onEdit && onEdit(resource);
                }}
                className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                تعديل الحقيبة
              </div>
            </div>
          </PermissionGuard>
          
          {/* 2. زر تغيير حالة النشر */}
          <div className="relative group overflow-visible flex items-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onStatusChange && onStatusChange(resource.id);
              }}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-400"
            >
              {resource.status === 'published' ? (
                <EyeIcon className="w-4 h-4 text-green-500" />
              ) : (
                <EyeOffIcon className="w-4 h-4" />
              )}
            </button>
            <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
              {resource.status === 'published' ? 'إخفاء الحقيبة' : 'نشر الحقيبة'}
            </div>
          </div>
          
          {/* 3. زر الحذف */}
          <PermissionGuard permission="delete_course_content">
            <div className="relative group overflow-visible flex items-center">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // فتح مودال الحذف
                  setShowDeleteModal(true);
                }}
                className="text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-400"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
              <div className="absolute invisible opacity-0 group-hover:visible group-hover:opacity-100 group-hover:delay-700 delay-0 bottom-full -translate-y-1 left-1/2 -translate-x-1/2 px-2 py-1 text-sm bg-gray-900 text-white rounded shadow-lg whitespace-nowrap z-[60] transition-all duration-75">
                حذف الحقيبة
              </div>
            </div>
          </PermissionGuard>
        </div>
      </div>
      
      {/* مودال تأكيد الحذف */}
      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => {
          onDelete && onDelete(resource.id);
          setShowDeleteModal(false);
        }}
        itemType="الحقيبة التعليمية"
        itemTitle={resource.title}
      />
    </>
  );
}
